import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, X, Send, Loader2 } from 'lucide-react';
import { useUpdate } from '../contexts/UpdateContext';
import React, { useState, useEffect } from 'react';

export function FeedbackButton({ courseId, route, organisationId }: { courseId?: string, route?: string, organisationId?: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button 
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-full shadow-lg flex items-center justify-center transition-all gap-2"
        title="Send Feedback"
      >
        <MessageSquare className="w-5 h-5" />
        <span className="font-medium whitespace-nowrap">
          Send Feedback
        </span>
      </button>

      {isOpen && (
        <FeedbackModal 
          onClose={() => setIsOpen(false)} 
          courseId={courseId} 
          route={route} 
          organisationId={organisationId} 
        />
      )}
    </>
  );
}

function FeedbackModal({ onClose, courseId, route, organisationId }: { onClose: () => void, courseId?: string, route?: string, organisationId?: string }) {
  const { user } = useAuth();
  const { setBusyState } = useUpdate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    tryingToDo: '',
    workedWell: '',
    brokeOrConfused: '',
    matchedDocument: '',
    tutorAnsweredCorrectly: '',
    useForTraining: '',
    email: user?.email || ''
  });

  useEffect(() => {
    const hasInput = Object.values(formData).some(v => v.length > 5);
    setBusyState('feedback', hasInput && !success);
    return () => setBusyState('feedback', false);
  }, [formData, success, setBusyState]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await addDoc(collection(db, 'pilot_feedback'), {
        ...formData,
        userId: user?.uid || 'anonymous',
        userEmail: user?.email || formData.email,
        organisationId: organisationId || null,
        courseId: courseId || null,
        route: route || window.location.pathname,
        createdAt: serverTimestamp(),
        browser: navigator.userAgent,
        deviceType: /Mobile|iP(hone|od|ad)|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
      });
      setSuccess(true);
      setTimeout(() => onClose(), 2000);
    } catch (err: any) {
      console.error('Failed to submit feedback', err);
      setError('Failed to submit feedback. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-bg-main/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-bg-alt border border-border-main rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-4 border-b border-border-main">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-500" /> Pilot Feedback
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-bg-main rounded-full text-text-alt transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {success ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 text-emerald-500 rounded-full flex items-center justify-center mb-4">
                <Send className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Thank you!</h3>
              <p className="text-text-alt">Your pilot feedback has been securely submitted.</p>
            </div>
          ) : (
            <form id="feedback-form" onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">{error}</div>}
              
              <div>
                <label className="block text-sm font-medium mb-1">What were you trying to do?</label>
                <textarea name="tryingToDo" value={formData.tryingToDo} onChange={handleChange} required className="w-full bg-bg-main border border-border-main rounded-xl p-3 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none h-20" placeholder="e.g., I tried to create a course from a 50-page PDF." />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">What worked well?</label>
                <textarea name="workedWell" value={formData.workedWell} onChange={handleChange} required className="w-full bg-bg-main border border-border-main rounded-xl p-3 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none h-20" placeholder="e.g., The course was generated very fast." />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">What broke or confused you?</label>
                <textarea name="brokeOrConfused" value={formData.brokeOrConfused} onChange={handleChange} className="w-full bg-bg-main border border-border-main rounded-xl p-3 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none h-20" placeholder="e.g., I didn't understand the quiz section." />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Did the generated course match your document?</label>
                <input name="matchedDocument" value={formData.matchedDocument} onChange={handleChange} type="text" className="w-full bg-bg-main border border-border-main rounded-xl px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none" placeholder="Yes / No / Somewhat..." />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Did the AI tutor answer correctly?</label>
                <input name="tutorAnsweredCorrectly" value={formData.tutorAnsweredCorrectly} onChange={handleChange} type="text" className="w-full bg-bg-main border border-border-main rounded-xl px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Would you use this for training?</label>
                <input name="useForTraining" value={formData.useForTraining} onChange={handleChange} type="text" className="w-full bg-bg-main border border-border-main rounded-xl px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Optional contact email</label>
                <input name="email" value={formData.email} onChange={handleChange} type="email" className="w-full bg-bg-main border border-border-main rounded-xl px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none" placeholder="you@company.com" />
              </div>
            </form>
          )}
        </div>

        {!success && (
          <div className="p-4 border-t border-border-main flex justify-end gap-3">
            <button type="button" onClick={onClose} disabled={loading} className="px-4 py-2 hover:bg-bg-main text-text-main font-medium rounded-xl transition-colors">
              Cancel
            </button>
            <button type="submit" form="feedback-form" disabled={loading} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-colors flex items-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit 
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
